import { Component } from '@theme/component';

/** Shared presentation and copy control. Product progress follows confirmed cart section renders. */
class Timpani20Promotion extends Component {
  #timer;
  #label;
  #copying = false;
  #cartObserver;

  connectedCallback() {
    super.connectedCallback();
    if (this.dataset.mode !== 'product') return;
    this.#cartObserver?.disconnect();
    // Horizon already replaces this section after confirmed cart mutations and Back/Forward restores.
    // Read its server-computed collection eligibility; AJAX cart JSON has no collection membership.
    const cartRoot = document.getElementById('shopify-section-cart-drawer-section');
    if (!cartRoot) return;
    this.#cartObserver = new MutationObserver(() => this.#syncCartState(cartRoot));
    this.#cartObserver.observe(cartRoot, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-eligible-units'],
    });
    this.#syncCartState(cartRoot);
  }

  #syncCartState(cartRoot) {
    const state = cartRoot.querySelector('[data-timpani20-cart-state]');
    const units = state ? Number(state.dataset.eligibleUnits) :
      cartRoot.querySelector('.cart-drawer--empty') ? 0 : null;
    if (!Number.isSafeInteger(units) || units < 0 ||
        String(units) === this.dataset.eligibleUnits) return;
    this.#renderUnits(units);
  }

  #renderUnits(units) {
    const progress = Math.min(3, units);
    this.dataset.eligibleUnits = String(units);
    this.classList.toggle('tb138-promo--near', units === 2);
    this.toggleAttribute('data-ready', units >= 3);
    for (const state of this.querySelectorAll('[data-promo-state]')) {
      state.hidden = Number(state.dataset.promoState) !== progress;
    }
    const bar = this.querySelector('[role="progressbar"]');
    if (bar) {
      bar.setAttribute('aria-valuenow', String(progress));
      bar.setAttribute('aria-label', this.dataset.progressTemplate.replace('__COUNT__', String(units)));
    }
    const track = this.querySelector('.tb138-promo__track > span');
    if (track) track.style.width = (progress / 3 * 100) + '%';
    const count = this.querySelector('.tb138-promo__progress > span:last-child');
    if (count) count.textContent = progress + ' / 3';
    const copy = this.querySelector('[data-product-copy]');
    if (copy) copy.hidden = units < 3;
    if (units < 3) {
      const fallback = this.querySelector('input');
      if (fallback) fallback.hidden = true;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#timer);
    this.#cartObserver?.disconnect();
  }

  /** Copies the code without applying it or changing the cart. */
  async copyCode() {
    if (this.#copying) return;
    const label = this.querySelector('[data-copy-label]');
    const status = this.querySelector('[data-copy-status]');
    const fallback = this.querySelector('input');
    if (!label || !status) return;
    this.#label ??= label.textContent;
    this.#copying = true;
    clearTimeout(this.#timer);
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText('TIMPANI20');
      if (!this.isConnected) return;
      if (fallback instanceof HTMLInputElement) fallback.hidden = true;
      label.textContent = this.dataset.copied || '';
      status.textContent = this.dataset.copied || '';
      this.#timer = setTimeout(() => {
        label.textContent = this.#label || '';
        status.textContent = '';
      }, 1500);
    } catch {
      if (!this.isConnected) return;
      if (fallback instanceof HTMLInputElement) {
        fallback.hidden = false;
        fallback.focus();
        fallback.select();
      }
      status.textContent = this.dataset.manual || '';
    } finally {
      this.#copying = false;
    }
  }
}

if (!customElements.get('timpani20-promotion')) {
  customElements.define('timpani20-promotion', Timpani20Promotion);
}
